import React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';

/**
 * SectionHead — shared home-page section header with italic accent + optional
 * meta row (link, count, byline). Spec: index.html `.section-head`.
 *
 * Title can include a pre-parsed accent via the `accent` prop, which renders
 * as an accessible teal italic on light surfaces.
 */

export interface SectionHeadProps {
  title: string;
  /** Second half of the title to render in italic teal. */
  accent?: string;
  /** Meta row, right-aligned — can contain arbitrary text + links. */
  meta?: React.ReactNode;
  /** Shortcut: render a simple link on the right. */
  linkHref?: string;
  linkLabel?: string;
  id?: string;
  className?: string;
}

export function SectionHead({
  title,
  accent,
  meta,
  linkHref,
  linkLabel,
  id,
  className,
}: SectionHeadProps) {
  return (
    <div
      id={id}
      className={cn(
        'flex items-baseline justify-between mb-8 pt-4.5 border-t-2 border-warm-navy',
        className
      )}
    >
      <h2
        className="font-display font-medium text-[34px] leading-[1.05] tracking-[-0.018em] text-warm-navy m-0"
        style={{ fontVariationSettings: '"opsz" 48' }}
      >
        {title}
        {accent && (
          <>
            {' '}
            <em className="italic font-medium text-aviado-teal-text">{accent}</em>
          </>
        )}
      </h2>
      <div className="font-mono text-[11.5px] tracking-[0.04em] text-slate">
        {meta}
        {linkHref && linkLabel && (
          <Link
            href={linkHref}
            className="ml-4 text-aviado-blue no-underline hover:text-warm-navy transition-colors duration-fast"
          >
            {linkLabel}
          </Link>
        )}
      </div>
    </div>
  );
}

export default SectionHead;
